import type { LobbyGame } from '../api/getLobby';
import { isPlayableLobbyGame } from './lobbyPlayableStatus';

/** DK embed URL flags that gate which game types may appear in the lobby. */
export type LobbyEmbedPermissions = {
  showDailyDraw?: boolean | null;
  showSquares?: boolean | null;
};

/** Daily Reveal / Daily Draw contests gated by DK's ShowDailyDraw embed param. */
export function isDailyDrawLobbyGame(game: LobbyGame): boolean {
  const gt = (game.game_type || '').trim().toLowerCase();
  return gt === 'daily_reveal' || gt === 'daily_draw';
}

/** Squares contests gated by DK's ShowSquares embed param. */
export function isSquaresLobbyGame(game: LobbyGame): boolean {
  return (game.game_type || '').trim().toLowerCase() === 'squares';
}

function searchParamsFromInput(searchParams?: URLSearchParams | string | null): URLSearchParams {
  if (searchParams == null) {
    return new URLSearchParams(typeof window !== 'undefined' ? window.location.search : '');
  }
  if (typeof searchParams === 'string') {
    return new URLSearchParams(searchParams.startsWith('?') ? searchParams.slice(1) : searchParams);
  }
  return searchParams;
}

function parseEmbedBoolParam(
  paramName: string,
  searchParams?: URLSearchParams | string | null
): boolean | null {
  const raw = searchParamsFromInput(searchParams).get(paramName);
  if (raw === null) return null;

  const normalized = raw.trim().toLowerCase();
  if (normalized === 'true' || normalized === '1') return true;
  if (normalized === 'false' || normalized === '0') return false;
  return null;
}

/**
 * Parse ShowDailyDraw from the lobby URL query string.
 * DK passes ShowDailyDraw=true when the user has permission, false otherwise.
 */
export function parseShowDailyDrawParam(
  searchParams?: URLSearchParams | string | null
): boolean | null {
  return parseEmbedBoolParam('ShowDailyDraw', searchParams);
}

/**
 * Parse ShowSquares from the lobby URL query string.
 * DK passes ShowSquares=true when the user has permission, false otherwise.
 */
export function parseShowSquaresParam(
  searchParams?: URLSearchParams | string | null
): boolean | null {
  return parseEmbedBoolParam('ShowSquares', searchParams);
}

/** Back-compat: legacy call sites pass only the Daily Draw flag. */
export function normalizeEmbedPermissions(
  permissions: LobbyEmbedPermissions | boolean | null | undefined
): LobbyEmbedPermissions {
  if (permissions === true || permissions === false || permissions === null) {
    return { showDailyDraw: permissions };
  }
  return permissions ?? {};
}

function isEmbedGameTypeAllowed(flag: boolean | null | undefined): boolean {
  // Omit param in local dev / legacy embeds — keep showing until DK always passes it.
  return flag !== false;
}

/** Whether Daily Draw games may appear in the lobby for this embed session. */
export function isDailyDrawAllowed(showDailyDraw: boolean | null): boolean {
  return isEmbedGameTypeAllowed(showDailyDraw);
}

/** Whether Squares games may appear in the lobby for this embed session. */
export function isSquaresAllowed(showSquares: boolean | null): boolean {
  return isEmbedGameTypeAllowed(showSquares);
}

function passesEmbedGameTypeFilters(game: LobbyGame, permissions: LobbyEmbedPermissions): boolean {
  const { showDailyDraw, showSquares } = permissions;
  if (!isEmbedGameTypeAllowed(showDailyDraw) && isDailyDrawLobbyGame(game)) return false;
  if (!isEmbedGameTypeAllowed(showSquares) && isSquaresLobbyGame(game)) return false;
  return true;
}

/** Visible, playable lobby game respecting DK embed permissions from the parent frame. */
export function isLobbyGameVisible(
  game: LobbyGame,
  permissions: LobbyEmbedPermissions | boolean | null = {}
): boolean {
  const normalized = normalizeEmbedPermissions(permissions);
  if (!game.is_visible || !isPlayableLobbyGame(game)) return false;
  return passesEmbedGameTypeFilters(game, normalized);
}

/**
 * Section row games are resolved from the tenant contest pool.
 * Legacy lobby_games visibility does not gate section fill — only playability and embed flags.
 */
export function isSectionLobbyGameEligible(
  game: LobbyGame,
  permissions: LobbyEmbedPermissions | boolean | null = {}
): boolean {
  const normalized = normalizeEmbedPermissions(permissions);
  if (!isPlayableLobbyGame(game)) return false;
  return passesEmbedGameTypeFilters(game, normalized);
}
